import { Injectable, inject, computed, signal, effect } from "@angular/core";
import { ConnectivityService } from "../network/services/connectivity.service";
import { OfflineTransactionDao } from "../../services/dao/offline-transaction.dao";
import { map, Observable, take } from "rxjs";
import { LocalizationService, localize } from "@rinami/cc7-ng";

@Injectable({
  providedIn: "root",
})
export class ApplicationContext {
  private readonly connectivityService = inject(ConnectivityService);
  private readonly transactionDao = inject(OfflineTransactionDao);
  private readonly localizationService = inject(LocalizationService);

  /**
   * Tracks the active language so computed, localized text is re-evaluated when the language changes.
   */
  private readonly currentLanguage = this.localizationService.language;

  /**
   * Internal signal to track the presence of unsynced transactions.
   */
  private readonly hasUnsyncedTransactions = signal<boolean>(false);

  /**
   * Property indicating the device is physically offline (no internet connection).
   */
  readonly deviceOffline = computed(() => !this.connectivityService.isOnline());

  /**
   * Property indicating the application is in an "offline state" due to pending (unsynced) transactions.
   */
  readonly appOffline = computed(() => this.hasUnsyncedTransactions());

  /**
   * Flag used while replaying queued offline transactions, so the app-offline guard does not block the sync itself.
   */
  readonly offlineSyncInProgress = signal(false);

  readonly isApplicationAndDeviceOnline = computed(() => !this.deviceOffline() && !this.appOffline());

  setOfflineSyncInProgress(inProgress: boolean): void {
    this.offlineSyncInProgress.set(inProgress);
  }

  /**
   * Unified banner text based on current connectivity and transaction state.
   */
  readonly offlineBannerText = computed(() => {
    this.currentLanguage();
    if (this.deviceOffline()) {
      return localize("common.banner.deviceOffline");
    }
    if (this.appOffline()) {
      return localize("common.banner.pendingOfflineTransactions");
    }
    return null;
  });

  constructor() {
    // Periodically or on storage changes check for unsynced transactions.
    // In a real scenario, we might want to trigger this check more strategically.
    this.refreshOfflineState().subscribe();

    // Re-check whenever we go online to see if we need to notify about pending syncs.
    effect(() => {
      if (this.connectivityService.isOnline()) {
        this.refreshOfflineState().subscribe();
      }
    });
  }

  /**
   * Refreshes the internal state regarding unsynced transactions.
   * Can be called manually after saving/deleting transactions.
   */
  refreshOfflineState(): Observable<void> {
    return this.transactionDao.fetchAll().pipe(
      take(1),
      map(transactions => {
        this.hasUnsyncedTransactions.set(transactions.length > 0);
        return void 0;
      })
    );
  }
}
